import type { Message } from '@/lib/agents/types';
import { Markdown } from './markdown/Markdown';
import { ReasoningBlock } from './ReasoningBlock';
import { ToolCallCard } from './ToolCallCard';
import { findModel } from '@/lib/providers/registry';
import { formatTime } from '@/lib/utils';
import { Sparkles } from 'lucide-react';

interface Props {
  message: Message;
  /** True while the session is actively streaming this (latest) message. */
  streaming?: boolean;
}

export function AssistantMessage({ message, streaming = false }: Props) {
  const modelInfo = message.model ? findModel(message.model) : undefined;

  // Find the index of the last reasoning part so we can mark only IT as
  // currently streaming (interleaved thinking: earlier reasoning blocks
  // are already done once a later text or reasoning part has appeared).
  let lastReasoningIdx = -1;
  message.parts.forEach((p, i) => { if (p.type === 'reasoning') lastReasoningIdx = i; });
  // The very last part overall — only that one is "live" while streaming.
  const lastPartIdx = message.parts.length - 1;

  return (
    <article className="group relative animate-fade-in py-3">
      <header className="mb-1.5 flex items-center gap-2 text-[12px] text-fg-subtle">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/10 text-accent">
          <Sparkles className="h-3 w-3" />
        </span>
        <span className="font-medium text-fg">Assistant</span>
        {modelInfo && (
          <span className="text-fg-muted">· {modelInfo.model.label}</span>
        )}
        <span className="ml-auto tabular-nums">{formatTime(message.createdAt)}</span>
      </header>

      <div className="space-y-1">
        {message.parts.map((part, i) => {
          if (part.type === 'text') {
            return <Markdown key={i} text={part.text} />;
          }
          if (part.type === 'reasoning') {
            const live = streaming && i === lastReasoningIdx && i === lastPartIdx;
            return <ReasoningBlock key={i} text={part.text} streaming={live} />;
          }
          if (part.type === 'tool-call') {
            return <ToolCallCard key={i} toolCall={part.toolCall} />;
          }
          return null;
        })}
      </div>

      {message.trailingStatus && (
        <p className="mt-2 text-[12px] text-fg-subtle">{message.trailingStatus}</p>
      )}
    </article>
  );
}
